import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import type { User, Tenant, AuthState, UserRole } from '../shared/types';
import { PROJECT_TYPE } from '../shared/constants/project';
import { PERMISSION, MODULE, modulesForPermissions, managePermissionFor } from '../shared/constants/permissions';
import { secureStorage } from '../utils';
import { loginApi, isTokenExpired } from '../api/authApi';
import type { LoginResponse } from '../api/authApi';
import { queryClient } from '../app/providers';
import { TOAST_MESSAGES } from '../shared/constants/strings';

// ── Context Interface ───────────────────────────────────────────────

interface AuthContextValue extends AuthState {
  /** Does the user hold this exact permission? */
  hasPermission: (permission: string) => boolean;
  /** Does the user hold at least one of these? */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** May the user write in this module, as opposed to merely reading it? */
  canManage: (module: string) => boolean;
  ownClientId: string | undefined;
  /** Works across every client rather than belonging to one, and may see them. */
  worksAcrossClients: boolean;
  isModuleEnabled: (module: string) => boolean;
  isIntegrationEnabled: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  finalizeLogin: (response: LoginResponse) => void;
  logout: () => Promise<void>;
}

// ── Helpers ─────────────────────────────────────────────────────────

interface ValidatedLoginResponse {
  token: string;
  userId: string;
  email: string;
  role: UserRole;
  tenantId?: string | null;
  refreshToken?: string;
  project?: LoginResponse['project'];
  permissions?: string[];
}

function validateLoginResponse(response: LoginResponse): ValidatedLoginResponse {
  if (!response.token || !response.userId || !response.email || !response.role) {
    throw new Error('Invalid login response');
  }
  return {
    token: response.token,
    userId: response.userId,
    email: response.email,
    role: response.role,
    tenantId: response.tenantId,
    refreshToken: response.refreshToken,
    project: response.project,
    permissions: response.permissions,
  };
}

function buildUserFromLoginResponse(response: ValidatedLoginResponse): User {
  const permissions = response.permissions ?? [];
  let firstName = '';
  let lastName = '';

  if (response.email) {
    const emailName = response.email.split('@')[0] ?? '';
    const nameParts = emailName.split('.');
    firstName = nameParts[0] ? nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1) : '';
    lastName = nameParts[1] ? nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1) : '';
  }

  return {
    id: response.userId,
    /**
     * Kept exactly as the server sent it, null included. Standing a `'global'` placeholder in for
     * "no client company" made every `user.tenantId` check read as though the user belonged to
     * one, and sent that literal to the API as a real tenant — which the backend rejects.
     */
    tenantId: response.tenantId ?? null,
    email: response.email,
    name: `${firstName} ${lastName}`.trim(),
    firstName,
    lastName,
    country: '',
    role: response.role,
    status: 'active',
    createdAt: new Date().toISOString(),
    permissions,
    // Derived rather than stored: a module is reachable when the user holds any permission for it.
    enabledModules: modulesForPermissions(permissions),
    project: response.project ?? null,
  };
}

function buildTenantFromLoginResponse(response: ValidatedLoginResponse): Tenant {
  // A platform-level account belongs to no client company, which the backend signals with a null
  // tenantId rather than with the role.
  if (!response.tenantId) {
    return { id: 'global', name: 'MGS Platform', slug: 'mgs', plan: 'enterprise', createdAt: '2023-01-01' };
  }
  return {
    id: response.tenantId || 'unknown',
    name: response.tenantId || 'Tenant',
    slug: response.tenantId || 'tenant',
    plan: 'professional',
    createdAt: new Date().toISOString(),
  };
}

// ── Context ─────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = secureStorage.getAccessToken();
    const session = secureStorage.getSession();

    if (token && !isTokenExpired(token) && session?.isLoggedIn) {
      // Restore from stored session data
      const restoredResponse: ValidatedLoginResponse = {
        token,
        userId: session.userId,
        email: session.email,
        role: session.role as UserRole,
        tenantId: session.tenantId,
        project: session.project,
        permissions: session.permissions,
      };
      setUser(buildUserFromLoginResponse(restoredResponse));
      setTenant(buildTenantFromLoginResponse(restoredResponse));
    } else if (token) {
      // Token expired or no session — clear everything
      secureStorage.clearAll();
    }
    setIsLoading(false);
  }, []);

  const isAuthenticated = !!user;

  const permissions = useMemo<string[]>(() => user?.permissions ?? [], [user]);

  const hasPermission = useCallback((permission: string): boolean => permissions.includes(permission), [permissions]);

  const hasAnyPermission = useCallback(
    (checkPermissions: string[]): boolean => checkPermissions.some((p) => permissions.includes(p)),
    [permissions],
  );

  const ownClientId = useMemo(() => user?.tenantId || undefined, [user]);

  const worksAcrossClients = useMemo(
    () => !ownClientId && permissions.includes(PERMISSION.CLIENTS_VIEW),
    [ownClientId, permissions],
  );

  const canManage = useCallback(
    (module: string): boolean => {
      const permission = managePermissionFor(module);
      return !!permission && permissions.includes(permission);
    },
    [permissions],
  );

  const isIntegrationProject = useMemo(() => {
    if (!user) return false;
    if (!user.project) return true;
    if (user.project.type !== PROJECT_TYPE.INTEGRATION) return false;
    return user.project.integrationsEnabled !== false;
  }, [user]);

  /** The project carries integrations *and* the user is allowed to see them. */
  const isIntegrationEnabled = useMemo(
    () => isIntegrationProject && permissions.includes(PERMISSION.INTEGRATIONS_VIEW),
    [isIntegrationProject, permissions],
  );

  /**
   * Two axes, both free of roles: the permissions decide which modules the user reaches, and the
   * project type decides whether the integration-backed ones exist for them at all.
   */
  const isModuleEnabled = useCallback(
    (module: string): boolean => {
      if ((module === MODULE.INTEGRATION || module === MODULE.EVENT_LOG) && !isIntegrationProject) {
        return false;
      }
      return (user?.enabledModules ?? []).includes(module);
    },
    [user, isIntegrationProject],
  );

  const finalizeLogin = useCallback((response: LoginResponse) => {
    const validated = validateLoginResponse(response);

    secureStorage.setTokens(validated.token, validated.refreshToken ?? '');
    secureStorage.setSession({
      isLoggedIn: true,
      userId: validated.userId,
      email: validated.email,
      role: validated.role,
      tenantId: validated.tenantId || null,
      project: validated.project ?? null,
      permissions: validated.permissions ?? [],
    });

    const loggedInUser = buildUserFromLoginResponse(validated);
    const loggedInTenant = buildTenantFromLoginResponse(validated);

    queryClient.clear();
    setUser(loggedInUser);
    setTenant(loggedInTenant);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const response = await loginApi({ email, password });

      if (!response.requires2FA) {
        finalizeLogin(response);
      }

      return response;
    },
    [finalizeLogin],
  );

  const logout = useCallback(async (): Promise<void> => {
    secureStorage.clearAll();
    queryClient.clear();
    setUser(null);
    setTenant(null);
    toast.success(TOAST_MESSAGES.LOGOUT_SUCCESS);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      tenant,
      isAuthenticated,
      isLoading,
      isIntegrationEnabled,
      hasPermission,
      hasAnyPermission,
      canManage,
      ownClientId,
      worksAcrossClients,
      isModuleEnabled,
      login,
      finalizeLogin,
      logout,
    }),
    [
      user,
      tenant,
      isAuthenticated,
      isLoading,
      isIntegrationEnabled,
      hasPermission,
      hasAnyPermission,
      canManage,
      ownClientId,
      worksAcrossClients,
      isModuleEnabled,
      login,
      finalizeLogin,
      logout,
    ],
  );

  return React.createElement(AuthContext.Provider, { value }, children);
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
